// src/components/layout/Footer/Footer.tsx
import React, { useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { useUpdateUserRoleMutation } from '../../../../api/userApi2';
import { useUserStore } from '../../../../app/store/useUserStore';
import { getRoleIdFromDefaultRoles, UserRole as UserRoleEnum } from '../../../../types/user';
import { Logo } from '../../common/Logo';
import { SocialLinks } from './SocialLinks';
import { SubscribeForm } from './SubscribeForm';
import type { FooterProps } from './Footer.types';
import translations from '../../../translations/en.json';
import type { UserRole } from '../../../../core/routes/types';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import {colors} from "../../../../shared/styles/theme/colors";



interface FooterPropsWithUserType extends FooterProps {
  userType: UserRole | 'generic';
}

export const Footer: React.FC<FooterPropsWithUserType> = ({ onSubscribe, userType = 'traveller' }) => {
  const [appVersion, setAppVersion] = useState<string>('');
  const copyrightText = translations.footer.copyright.replace('{{year}}', String(new Date().getFullYear()));

  useEffect(() => {
    fetch('/version.json?t=' + Date.now())
      .then((res) => res.json())
      .then((data) => setAppVersion(data.version ?? ''))
      .catch(() => {});
  }, []);

  const { theme } = useTheme();
  const { isAuthenticated } = useAuth0();
  const { user, updateUserRole: updateUserRoleInStore, defaultRoles } = useUserStore();
  const updateUserRoleMutation = useUpdateUserRoleMutation();
  const navigate = useNavigate();

  const handleRoleToggle = async () => {
    if (!isAuthenticated || !user?.user_id) {
      navigate(`/${userType === 'sender' ? 'traveller' : 'sender'}`);
      return;
    }
    try {
      const newRole = (userType === "sender" ? UserRoleEnum.TRAVELLER : UserRoleEnum.SENDER);
      const newRoleId = getRoleIdFromDefaultRoles(newRole, defaultRoles);
      await updateUserRoleMutation.mutateAsync({
        user_id: user.user_id,
        role_id: newRoleId,
      });
      updateUserRoleInStore(newRole);
      window.location.href = '/';
    } catch {
      alert('Failed to switch role. Please try again.');
    }
  };

  const footerBgColor = userType === 'sender'
    ? colors.sender.footerBg
    : userType === 'generic'
    ? colors.generic.footerBg
    : colors.traveller.footerBg;

  const FOOTER_COLORS = {
    background: footerBgColor,
    text: '#FFFFFF',
    divider: 'rgba(255, 255, 255, 0.3)',
  } as const;

  const footerLinks = {
    quicklinks: [
      { label: translations.common.home, href: isAuthenticated ? '/home' : '/' },
      { label: translations.common.about, href: 'https://skyporter.us/about' },
      { label: translations.common.contact, href: 'https://skyporter.us/contact' },
    ],
    legal: [
      { label: translations.footer.terms, href: 'https://skyporter.us/term' },
      { label: translations.footer.privacy, href: 'https://skyporter.us/privacy' },
      { label: translations.footer.cancellation, href: 'https://skyporter.us/cancellation' },
    ],
    compact: [
      { label: translations.footer.faqs, href: 'https://skyporter.us/faqs' },
      { label: translations.footer.cancellation, href: 'https://skyporter.us/cancellation' },
    ],
  };

  // Compact footer for logged-in sender/traveller users
  if (isAuthenticated && (userType === 'sender' || userType === 'traveller')) {
    return (
      <Box
        component="footer"
        sx={{
          bgcolor: FOOTER_COLORS.background,
          borderTopLeftRadius: { xs: '24px', xl: '28px', xxl: '32px' },
          borderTopRightRadius: { xs: '24px', xl: '28px', xxl: '32px' },
          color: FOOTER_COLORS.text,
          py: { xs: 1.5, lg: 2 },
          px: { xs: 4, md: 5, xl: 6, xxl: 7 },
          '@media (max-width: 310px) and (max-height: 300px)': {
            px: 1.5,
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            maxWidth: {
              xs: '100%',
              md: '100%',
              lg: '1440px',
              xl: '1560px',
              xxl: '1680px'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              justifyContent: { xs: 'center', lg: 'space-between' },
              alignItems: 'center',
              gap: { xs: 0.5, lg: 0 },
            }}
          >
            {/* Social Icons */}
            <Box sx={{ order: { xs: 1, lg: 0 } }}>
              <SocialLinks />
            </Box>

            {/* Divider — only shown when stacked on smaller screens */}
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                width: '100%',
                maxWidth: 280,
                height: 1,
                bgcolor: FOOTER_COLORS.divider,
                my: 0.5,
                order: 2,
              }}
            />

            {/* Logo + Version */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                order: { xs: 0, lg: 1 },
              }}
            >
              <Logo
                variant="light"
                sx={{ width: { xs: 90, lg: 140, xl: 152, xxl: 164 }, height: { xs: 27, lg: 41, xl: 45, xxl: 48 } }}
              />
              <Box
                sx={{
                  fontFamily: theme.typography.bodyText.fontFamily,
                  fontSize: { xs: 11, xl: 12, xxl: 13 },
                  fontWeight: 400,
                  color: 'white',
                  opacity: 0.85,
                }}
              >
                WebApp Version {appVersion}
              </Box>
            </Box>

            {/* Divider — only shown when stacked on smaller screens */}
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                width: '100%',
                maxWidth: 280,
                height: 1,
                bgcolor: FOOTER_COLORS.divider,
                my: 0.5,
                order: 2,
              }}
            />

            {/* Compact links */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, lg: 3, xl: 3.5 },
                order: { xs: 3, lg: 2 },
              }}
            >
              {footerLinks.compact.map((link) => (
                <Box
                  key={link.label}
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontFamily: theme.typography.bodyText.fontFamily,
                    fontSize: { xs: 11, lg: 13, xl: 14, xxl: 15 },
                    fontWeight: 400,
                    color: 'white',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Copyright */}
          <Box
            sx={{
              fontFamily: theme.typography.bodyText.fontFamily,
              fontSize: { xs: 12, xl: 13, xxl: 14 },
              fontWeight: 400,
              color: 'white',
              textAlign: 'center',
              mt: { xs: 1, lg: 0.75 },
            }}
          >
            {copyrightText}
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: FOOTER_COLORS.background,
        borderTopLeftRadius: { xs: '24px', xl: '28px', xxl: '32px' },
        borderTopRightRadius: { xs: '24px', xl: '28px', xxl: '32px' },
        color: FOOTER_COLORS.text,
        py: { xs: 2.5, sm: 3, md: 3.5, xl: 6, xxl: 7 },
        px: { xs: 2.5, sm: 3, md: 3.5, xl: 6, xxl: 7 },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          maxWidth: {
            xs: '100%',
            md: '100%',
            lg: '1440px',
            xl: '1560px',
            xxl: '1680px'
          }
        }}
      >
        {/* Top Section: Logo & Social Icons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 2.5, md: 3.5, xl: 5, xxl: 6 },
          }}
        >
          <Logo
            variant="light"
            width={{ xs: 40, md: 50, xl: 60, xxl: 70 } as any}
            height={{ xs: 12, md: 15, xl: 18, xxl: 21 } as any}
          />
          <SocialLinks />
        </Box>

        {/* Main Content Grid */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: { xs: 2.5, md: 3, lg: 5, xl: 6, xxl: 7 },
            mb: { xs: 2.5, md: 3.5, xl: 5, xxl: 6 },
            flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap' },
          }}
        >
          {/* Description */}
          <Box
            sx={{
              maxWidth: { xs: '100%', md: '100%', lg: 450, xl: 500, xxl: 550 },
              flex: { lg: '1 1 auto' },
              width: { xs: '100%', md: '100%', lg: 'auto' },
              fontFamily: theme.typography.bodyText.fontFamily,
              fontSize: { xs: 13, sm: 14, md: 16, lg: 18, xl: 19, xxl: 20 },
              fontWeight: 400,
              lineHeight: { xs: '19px', sm: '21px', md: '24px', lg: '27px', xl: '29px', xxl: '30px' },
              color: 'white',
            }}
          >
            {translations.footer.description}
          </Box>

          {/* Quicklinks */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 0.5, md: 0.75, xl: 1.25, xxl: 1.5 },
              minWidth: { xs: '48%', md: '48%', lg: 134, xl: 145, xxl: 156 },
              width: { xs: '48%', md: '48%', lg: 'auto' },
              flex: { lg: '0 0 auto' },
            }}
          >
            <Box
              component="h3"
              sx={{
                fontFamily: theme.typography.sectionSubheading.fontFamily,
                fontSize: { xs: 13, sm: 14, md: 16, lg: 18, xl: 19, xxl: 20 },
                fontWeight: 600,
                lineHeight: { xs: '19px', sm: '21px', md: '24px', lg: '27px' },
                m: 0,
                mb: 0.5,
              }}
            >
              {translations.footer.quickLinks}
            </Box>
            {footerLinks.quicklinks.map((link) => (
              <Box
                key={link.label}
                component={Link}
                to={link.href}
                sx={{
                  fontFamily: theme.typography.bodyText.fontFamily,
                  fontSize: { xs: 11, sm: 12, md: 13, lg: 16, xl: 17, xxl: 18 },
                  fontWeight: 400,
                  lineHeight: { xs: '16px', sm: '18px', md: '19px', lg: '24px' },
                  color: 'white',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </Box>
            ))}
            {/* Role Toggle Button - Hide for generic users */}
            {userType !== 'generic' && (
              <Box
                component="button"
                onClick={handleRoleToggle}
                disabled={isAuthenticated && updateUserRoleMutation.isPending}
                sx={{
                  fontFamily: theme.typography.bodyText.fontFamily,
                  fontSize: { xs: 11, sm: 12, md: 13, lg: 16, xl: 17, xxl: 18 },
                  fontWeight: 400,
                  lineHeight: { xs: '16px', sm: '18px', md: '19px', lg: '24px' },
                  color: 'white',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                {userType === 'sender'
                  ? translations.footer.switchTotraveller
                  : translations.footer.switchToSender}
              </Box>
            )}
          </Box>

          {/* Legal Policies */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 0.5, md: 0.75, xl: 1.25, xxl: 1.5 },
              minWidth: { xs: '48%', md: '48%', lg: 156, xl: 168, xxl: 180 },
              width: { xs: '48%', md: '48%', lg: 'auto' },
              flex: { lg: '0 0 auto' },
            }}
          >
            <Box
              component="h3"
              sx={{
                fontFamily: theme.typography.sectionSubheading.fontFamily,
                fontSize: { xs: 13, sm: 14, md: 16, lg: 18, xl: 19, xxl: 20 },
                fontWeight: 600,
                lineHeight: { xs: '19px', sm: '21px', md: '24px', lg: '27px' },
                m: 0,
                mb: 0.5,
              }}
            >
              {translations.footer.legalPolicies}
            </Box>
            {footerLinks.legal.map((link) => (
              <Box
                key={link.label}
                component={Link}
                to={link.href}
                sx={{
                  fontFamily: theme.typography.bodyText.fontFamily,
                  fontSize: { xs: 11, sm: 12, md: 13, lg: 16, xl: 17, xxl: 18 },
                  fontWeight: 400,
                  lineHeight: { xs: '16px', sm: '18px', md: '19px', lg: '24px' },
                  color: 'white',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </Box>
            ))}
          </Box>

          {/* Subscribe */}
          <SubscribeForm onSubscribe={onSubscribe} />
        </Box>

        {/* Bottom Section: Copyright */}
        <Box sx={{ mt: { xs: 1.5, md: 2, xl: 2.5 } }}>
          <Box
            sx={{
              height: 1.2,
              bgcolor: FOOTER_COLORS.divider,
              mb: { xs: 1.5, md: 2, xl: 2.5 },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 0.75, sm: 2 },
              fontFamily: theme.typography.bodyText.fontFamily,
              fontSize: { xs: 10, sm: 11, md: 12, xl: 13, xxl: 14 },
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            <Box>{copyrightText}</Box>
            <Box><strong>{translations.footer.webappVersion}:</strong> {appVersion}</Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};